import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { Check, LayoutTemplate, Search } from 'lucide-react';

import {
    getBookkeepingTemplate,
    listBookkeepingTemplates,
    suggestVerification,
    toUserErrorMessage,
} from '../../../services/BookkeepingTemplateService';
import TemplateForm from '../BookkeepingTemplates/TemplateForm';
import { getTemplateAccountPreview } from './manualBookkeepingUtils';

const CATEGORY_LABELS = {
    forsaljning: 'Försäljning',
    inkop_varor: 'Inköp av varor',
    inkop_tjanster: 'Inköp av tjänster',
    inventarier: 'Inventarier',
    personal_lon: 'Personal och lön',
    bil_transport: 'Bil och transport',
    representation: 'Representation',
    bank_avgifter: 'Bank, ränta och avgifter',
    skatt_moms: 'Skatt och moms',
    eget_kapital: 'Eget kapital',
    utlagg: 'Utlägg och privatkonton',
    periodiseringar: 'Periodiseringar och bokslut',
    ovrigt: 'Övrigt',
};

const categoryLabel = (category) => CATEGORY_LABELS[category]
    || String(category || 'Övrigt').replace(/_/g, ' ');

const initialValuesForTemplate = (template) => (template?.body?.inputs || []).reduce((values, input) => {
    if (input.default !== undefined && input.default !== null) values[input.name] = input.default;
    return values;
}, {});

const evaluationErrorMessage = (result) => {
    const detail = Array.isArray(result?.errors)
        ? result.errors.map((error) => String(error?.error || error?.message || '')).join(' ')
        : '';
    if (/required input|missing/i.test(detail)) return 'Fyll i alla obligatoriska fält.';
    if (/expects (a number|an integer)|finite number/i.test(detail)) return 'Kontrollera att beloppen innehåller giltiga siffror.';
    if (/account ref|account_band|account number/i.test(detail)) return 'Välj ett giltigt konto i mallens kontofält.';
    if (/imbalance|ΣD=/i.test(detail)) return 'Mallen gav ett obalanserat verifikat. Kontrollera värdena.';
    if (/auto-resolve side/i.test(detail)) return 'Mallen kan inte avgöra debet eller kredit för ett av de valda kontona.';
    return 'Mallen kunde inte beräkna konteringsraderna. Kontrollera värdena och försök igen.';
};

const ManualTemplatePickerModal = ({ show, onHide, onApply, companyId, accounts = [] }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(null);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('all');
    const [selectedId, setSelectedId] = useState(null);
    const [selectedTemplateDetails, setSelectedTemplateDetails] = useState(null);
    const [selectedTemplateLoading, setSelectedTemplateLoading] = useState(false);
    const [inputValues, setInputValues] = useState({});
    const selectionRequestRef = useRef(0);

    useEffect(() => {
        if (!show || !companyId) return undefined;

        let cancelled = false;
        setLoading(true);
        setError(null);
        setSearch('');
        setCategory('all');
        setSelectedId(null);
        setSelectedTemplateDetails(null);
        setSelectedTemplateLoading(false);
        setInputValues({});
        selectionRequestRef.current += 1;

        listBookkeepingTemplates(companyId)
            .then((rows) => {
                if (!cancelled) setTemplates(Array.isArray(rows) ? rows : []);
            })
            .catch((loadError) => {
                if (!cancelled) setError(toUserErrorMessage(loadError));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [show, companyId]);

    const categories = useMemo(() => [...new Set(
        templates.map((template) => template.category).filter(Boolean)
    )].sort((a, b) => categoryLabel(a).localeCompare(categoryLabel(b), 'sv')), [templates]);

    const filteredTemplates = useMemo(() => {
        const normalizedSearch = search.trim().toLocaleLowerCase('sv');
        return templates.filter((template) => {
            if (category !== 'all' && template.category !== category) return false;
            if (!normalizedSearch) return true;
            return [
                template.template_name,
                template.description,
            ]
                .filter(Boolean)
                .some((value) => String(value).toLocaleLowerCase('sv').includes(normalizedSearch));
        });
    }, [templates, search, category]);

    const templateGroups = useMemo(() => {
        const byName = (a, b) => String(a.template_name || '')
            .localeCompare(String(b.template_name || ''), 'sv');

        if (category !== 'all') {
            return [{
                key: category,
                label: categoryLabel(category),
                showHeader: false,
                templates: [...filteredTemplates].sort(byName),
            }];
        }

        const grouped = filteredTemplates.reduce((groups, template) => {
            const key = template.category || 'ovrigt';
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(template);
            return groups;
        }, new Map());

        return [...grouped.entries()]
            .sort(([categoryA], [categoryB]) => (
                categoryLabel(categoryA).localeCompare(categoryLabel(categoryB), 'sv')
            ))
            .map(([key, groupTemplates]) => ({
                key,
                label: categoryLabel(key),
                showHeader: true,
                templates: groupTemplates.sort(byName),
            }));
    }, [filteredTemplates, category]);

    const selectedIsVisible = filteredTemplates.some((template) => template.template_id === selectedId);
    const selectedTemplateSummary = templates.find((template) => template.template_id === selectedId) || null;
    const selectedTemplate = selectedTemplateDetails || selectedTemplateSummary;
    const selectedInputs = selectedTemplate?.body?.inputs || [];
    const missingRequiredInputs = selectedInputs.filter((input) => (
        input.required
        && (inputValues[input.name] === undefined
            || inputValues[input.name] === null
            || inputValues[input.name] === '')
    ));

    const handleSelect = async (template) => {
        if (template.template_id === selectedId) return;
        const requestId = selectionRequestRef.current + 1;
        selectionRequestRef.current = requestId;
        setSelectedId(template.template_id);
        setSelectedTemplateDetails(null);
        setSelectedTemplateLoading(true);
        setInputValues({});
        setError(null);

        try {
            const fullTemplate = await getBookkeepingTemplate(companyId, template.template_id);
            if (selectionRequestRef.current !== requestId) return;
            setSelectedTemplateDetails(fullTemplate);
            setInputValues(initialValuesForTemplate(fullTemplate));
        } catch (loadError) {
            if (selectionRequestRef.current !== requestId) return;
            setError(toUserErrorMessage(loadError));
        } finally {
            if (selectionRequestRef.current === requestId) setSelectedTemplateLoading(false);
        }
    };

    const handleApplyBlank = async () => {
        if (!selectedId || !selectedIsVisible) return;
        setApplying('blank');
        setError(null);
        try {
            const fullTemplate = await getBookkeepingTemplate(companyId, selectedId);
            await onApply(fullTemplate);
        } catch (applyError) {
            setError(applyError.response ? toUserErrorMessage(applyError) : applyError.message);
        } finally {
            setApplying(null);
        }
    };

    const handleEvaluateAndApply = async () => {
        if (!selectedId || !selectedIsVisible) return;
        if (missingRequiredInputs.length > 0) {
            setError(`Fyll i: ${missingRequiredInputs.map((input) => input.label).join(', ')}.`);
            return;
        }

        setApplying('calculated');
        setError(null);
        try {
            const fullTemplate = await getBookkeepingTemplate(companyId, selectedId);
            const result = await suggestVerification(companyId, selectedId, inputValues);
            if (!result?.ok || !Array.isArray(result.lines) || result.lines.length === 0) {
                setError(evaluationErrorMessage(result));
                return;
            }
            await onApply(fullTemplate, result);
        } catch (applyError) {
            setError(applyError.response ? toUserErrorMessage(applyError) : applyError.message);
        } finally {
            setApplying(null);
        }
    };

    return (
        <Modal show={show} onHide={applying ? undefined : onHide} size="lg" centered className="manual-template-modal">
            <Modal.Header closeButton={!applying}>
                <div>
                    <Modal.Title>Skapa rader från mall</Modal.Title>
                    <p className="manual-template-modal__subtitle">
                        Skapa tomma rader eller fyll i mallens uppgifter och låt den beräkna konteringen.
                    </p>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="manual-template-modal__controls">
                    <label className="manual-template-modal__search">
                        <Search size={15} aria-hidden="true" />
                        <span className="visually-hidden">Sök mallar</span>
                        <input
                            type="search"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Sök efter namn eller beskrivning…"
                            autoFocus
                        />
                    </label>
                </div>
                <div className="manual-template-modal__filters ll-chips" role="group" aria-label="Filtrera mallar på kategori">
                    <button
                        type="button"
                        className={`ll-chip ${category === 'all' ? 'active' : ''}`}
                        aria-pressed={category === 'all'}
                        onClick={() => setCategory('all')}
                    >
                        Alla
                    </button>
                    {categories.map((option) => (
                        <button
                            key={option}
                            type="button"
                            className={`ll-chip ${category === option ? 'active' : ''}`}
                            aria-pressed={category === option}
                            onClick={() => setCategory(option)}
                        >
                            {categoryLabel(option)}
                        </button>
                    ))}
                </div>

                {error && <div className="manual-template-modal__error" role="alert">{error}</div>}

                {loading ? (
                    <div className="manual-template-modal__state"><Spinner animation="border" size="sm" /> Laddar mallar…</div>
                ) : filteredTemplates.length === 0 ? (
                    <div className="manual-template-modal__state">
                        <LayoutTemplate size={24} />
                        <span>{templates.length === 0 ? 'Inga mallar är tillgängliga.' : 'Inga mallar matchar din sökning.'}</span>
                    </div>
                ) : (
                    <div className={`manual-template-modal__list ${selectedTemplate ? 'has-selection' : ''}`} role="list" aria-label="Bokföringsmallar">
                        {templateGroups.map((group) => (
                            <React.Fragment key={group.key}>
                                {group.showHeader && (
                                    <div className="manual-template-modal__group-header" role="heading" aria-level="3">
                                        {group.label}
                                    </div>
                                )}
                                {group.templates.map((template) => {
                                    const selected = selectedId === template.template_id;
                                    const templateAccounts = selected && selectedTemplateDetails
                                        ? getTemplateAccountPreview(selectedTemplateDetails, accounts)
                                        : [];
                                    return (
                                        <button
                                            key={template.template_id}
                                            type="button"
                                            aria-pressed={selected}
                                            className={`manual-template-modal__item ${selected ? 'is-selected' : ''}`}
                                            onClick={() => handleSelect(template)}
                                        >
                                            <span className="manual-template-modal__item-icon">
                                                {selected ? <Check size={16} /> : <LayoutTemplate size={16} />}
                                            </span>
                                            <span className="manual-template-modal__item-copy">
                                                <span className="manual-template-modal__item-name">{template.template_name}</span>
                                                {template.description && <span className="manual-template-modal__item-description">{template.description}</span>}
                                            </span>
                                            <span className="manual-template-modal__category">{categoryLabel(template.category)}</span>
                                            {selected && (
                                                <span className="manual-template-modal__accounts">
                                                    <span className="manual-template-modal__accounts-title">Konton i mallen</span>
                                                    {selectedTemplateLoading ? (
                                                        <span className="manual-template-modal__accounts-loading">
                                                            <Spinner animation="border" size="sm" /> Laddar konton…
                                                        </span>
                                                    ) : !selectedTemplateDetails ? (
                                                        <span className="manual-template-modal__accounts-empty">Kontona kunde inte laddas.</span>
                                                    ) : templateAccounts.length > 0 ? (
                                                        <span className="manual-template-modal__account-list">
                                                            {templateAccounts.map((account) => (
                                                                <span
                                                                    className="manual-template-modal__account-chip"
                                                                    key={account.key}
                                                                    title={[account.accountName, account.context].filter(Boolean).join(' — ') || undefined}
                                                                >
                                                                    {account.accountNumber || 'Valbart'}
                                                                </span>
                                                            ))}
                                                        </span>
                                                    ) : (
                                                        <span className="manual-template-modal__accounts-empty">Mallen innehåller inga konteringsrader.</span>
                                                    )}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </div>
                )}
                {!loading && templates.length > 0 && (
                    <div className="manual-template-modal__count">
                        Visar {filteredTemplates.length} av {templates.length} mallar
                    </div>
                )}
                {selectedTemplateSummary && selectedIsVisible && (
                    <section className="manual-template-modal__parameters" aria-labelledby="manual-template-parameters-title">
                        <div className="manual-template-modal__parameters-heading">
                            <div>
                                <h3 id="manual-template-parameters-title">Låt mallen beräkna raderna</h3>
                                <p>Fyll i uppgifterna så beräknas belopp, villkor och rätt konteringsrader.</p>
                            </div>
                            {selectedTemplateDetails && !selectedTemplateLoading && missingRequiredInputs.length === 0 && (
                                <span className="manual-template-modal__parameters-ready"><Check size={14} /> Klar att beräkna</span>
                            )}
                        </div>
                        {selectedTemplateLoading ? (
                            <div className="manual-template-modal__parameters-loading">
                                <Spinner animation="border" size="sm" /> Laddar mallens fält…
                            </div>
                        ) : selectedTemplateDetails ? (
                            <TemplateForm
                                template={selectedTemplateDetails}
                                values={inputValues}
                                onChange={(name, value) => {
                                    setInputValues((current) => ({ ...current, [name]: value }));
                                    setError(null);
                                }}
                                disabled={Boolean(applying)}
                                accounts={accounts}
                            />
                        ) : null}
                    </section>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={onHide} disabled={applying}>Avbryt</button>
                <button
                    type="button"
                    className="btn manual-template-modal__blank-action"
                    onClick={handleApplyBlank}
                    disabled={!selectedId || !selectedIsVisible || applying}
                >
                    {applying === 'blank' ? <><Spinner animation="border" size="sm" /> Skapar…</> : 'Skapa tomma rader'}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleEvaluateAndApply}
                    disabled={!selectedId || !selectedIsVisible || !selectedTemplateDetails || selectedTemplateLoading || missingRequiredInputs.length > 0 || applying}
                >
                    {applying === 'calculated' ? <><Spinner animation="border" size="sm" /> Beräknar…</> : 'Beräkna och använd'}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ManualTemplatePickerModal;
